<script setup>
import { computed, unref, watch } from 'vue';
import { VInput, VRadioButton } from '../../components';
import OrderSummary from '@/modules/Signup/components/sign-up-form/OrderSummary.vue';
import { useSignUpModuleStore } from '@/stores/modules/signup/signUpStore';
import { storeToRefs } from 'pinia/dist/pinia';
import { VButton, VCheckbox, VDropdown } from '@/modules/Signup/components';
import VModal from '@/modules/Signup/components/VModal.vue';
import { useModals } from '@/modules/Signup/composables/useModals';
import { CARD_TYPES } from '@/modules/Signup/constants/cardTypes';
import { useVLoader } from '@/modules/Signup/composables/useVLoader';
import VLoader from '@/modules/Signup/components/VLoader.vue';
import { useRouter } from 'vue-router';

const router = useRouter();

const
    signUpStore = useSignUpModuleStore(),
    { optPaymentMethods, banks, isPaymentRecurring, paymentForms } = storeToRefs(signUpStore),
    { updatePaymentForm } = signUpStore;

const { open, close, isOpen } = useModals();
const { isLoading, loadingText, withLoading } = useVLoader();
const cardTypes = CARD_TYPES;

watch(isPaymentRecurring, (newVal) => {
    if (!newVal) {
        open('modal-recurring');
    } else {
        console.log('should clear the recurring form data')
        // clear the recurring form data
    }
});

// Update form data
function useField(formType, field) {
    return computed({
        get: () => unref(paymentForms)[formType]?.[field] || '',
        set: (val) => updatePaymentForm(formType, field, val),
    });
}

// TODO: create a form wrapper
const
    upfrontSelectedPaymentMethod = useField('upfront', 'selectedPaymentMethod'),
    upfrontBankId = useField('upfront', 'bankId'),
    upfrontBankAccountName = useField('upfront', 'bankAccountName'),
    upfrontBankBsb = useField('upfront', 'bankBsb'),
    upfrontBankAccountNumber = useField('upfront', 'bankAccountNumber'),
    upfrontCcCardType = useField('upfront', 'ccCardType'),
    upfrontCcCardNumber = useField('upfront', 'ccCardNumber'),
    upfrontCcCardName = useField('upfront', 'ccCardName'),
    upfrontCcExpiryDate = useField('upfront', 'ccExpiryDate'),
    upfrontCcCvv = useField('upfront', 'ccCvv');

const
    recurringSelectedPaymentMethod = useField('recurring', 'selectedPaymentMethod'),
    recurringBankId = useField('recurring', 'bankId'),
    recurringBankAccountName = useField('recurring', 'bankAccountName'),
    recurringBankBsb = useField('recurring', 'bankBsb'),
    recurringBankAccountNumber = useField('recurring', 'bankAccountNumber'),
    recurringCcCardType = useField('recurring', 'ccCardType'),
    recurringCcCardNumber = useField('recurring', 'ccCardNumber'),
    recurringCcCardName = useField('recurring', 'ccCardName'),
    recurringCcExpiryDate = useField('recurring', 'ccExpiryDate'),
    recurringCcCvv = useField('recurring', 'ccCvv');


/**
 * When the user decided to not use recurring, close the modal and set the `isPaymentRecurring` to true.
 */
function cancelRecurringSelection() {
    isPaymentRecurring.value = true;
    close('modal-recurring');
}

const submit = async () => {
    try {
        const { operation, loadingText } = await signUpStore.processSelectedPaymentMethod();
        await withLoading(operation, loadingText);

        await router.push({
            name: 'signup',
            query: { from: 'payment-method', next: 'submit' },
        });
    } catch (error) {
        console.error(error);
    }
};
</script>

<template>
    <div class="tw-flex tw-items-start tw-gap-x-20">
        <div class="tw-flex-1 tw-flex tw-flex-col tw-gap-24">
            <div>
                <VRadioButton
                    v-model="upfrontSelectedPaymentMethod"
                    :options="optPaymentMethods"
                    label="Payment Method"
                    class="tw-mb-16"
                />

                <!-- @TODO: Create component for identifying if its a bank account or cc -->
                <!-- Bank account -->
                <div v-if="upfrontSelectedPaymentMethod.id === 1">
                    <VDropdown
                        v-model="upfrontBankId"
                        label="Select a bank"
                        :options="banks"
                    />
                    <VInput v-model="upfrontBankAccountName" label="Account Name" class="tw-my-8" required />
                    <VInput v-model="upfrontBankBsb" label="BSB" class="tw-my-8" required />
                    <VInput v-model="upfrontBankAccountNumber" label="Account Number" class="tw-my-8" required />
                </div>
                <!-- Credit Card -->
                <div v-if="upfrontSelectedPaymentMethod.id === 10">
                    <VDropdown
                        v-model="upfrontCcCardType"
                        label="Select a card type"
                        :options="cardTypes"
                        class="tw-my-8"
                        required
                    />
                    <VInput v-model="upfrontCcCardNumber" label="Card Number" class="tw-my-8" required />
                    <VInput v-model="upfrontCcCardName" label="Card Name" class="tw-my-8" required />
                    <VInput v-model="upfrontCcExpiryDate" label="Expiry" class="tw-my-8" placeholder="MM/YYYY" required />
                    <VInput v-model="upfrontCcCvv" label="CVV" class="tw-my-8" required />
                </div>
            </div>

            <VCheckbox v-model="isPaymentRecurring">
                Set this as the default payment method for recurring payments?
            </VCheckbox>
        </div>

        <OrderSummary />
    </div>


    <!--
        | =========================================
        |  Modal for Non-recurring payment
        | =========================================
    -->
    <VModal v-model="isOpen('modal-recurring').value">
        <template #title>
            <h4>Enter your payment details for recurring</h4>
        </template>

        <VRadioButton
            v-model="recurringSelectedPaymentMethod"
            :options="optPaymentMethods"
            label="Payment Method"
            class="tw-mb-16"
        />

        <!-- @TODO: Create component for identifying if its a bank account or cc -->
        <div v-if="recurringSelectedPaymentMethod.id === 1">
            <VDropdown
                v-model="recurringBankId"
                label="Select a bank"
                :options="banks"
            />
            <VInput v-model="recurringBankAccountName" label="Account Name" class="tw-my-8" required />
            <VInput v-model="recurringBankBsb" label="BSB" class="tw-my-8" required />
            <VInput v-model="recurringBankAccountNumber" label="Account Number" class="tw-my-8" required />
        </div>
        <div v-if="recurringSelectedPaymentMethod.id === 10">
            <VDropdown
                v-model="recurringCcCardType"
                label="Select a card type"
                :options="cardTypes"
                class="tw-my-8"
                required
            />
            <VInput v-model="recurringCcCardNumber" label="Card Number" class="tw-my-8" required />
            <VInput v-model="recurringCcCardName" label="Card Name" class="tw-my-8" required />
            <VInput
                v-model="recurringCcExpiryDate" label="Expiry" class="tw-my-8" placeholder="MM/YYYY" required
            />
            <VInput v-model="recurringCcCvv" label="CVV" class="tw-my-8" required />
        </div>

        <template #footer>
            <VButton label="Cancel" @click="cancelRecurringSelection" />
            <VButton label="Confirm" @click="submit" />
        </template>
    </VModal>

    <!-- Show loader during route transitions -->
    <VLoader
        v-if="isLoading"
        :text="loadingText"
    />
</template>
